import { useState, type ReactNode } from 'react';
import {
  Button,
  Container,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Title,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { MediaUsage, Me as MeType, Profile, ProfileUpdate } from '@nelnela/contracts';
import type { EnMode } from '../components/watch/Transcript';
import { useStudy } from '../app/study';
import { api } from '../lib/api';
import { authClient } from '../lib/authClient';
import { mediaCostLine, mediaUsageLine } from '../lib/text';
import { isStandalone, purgeUserCaches, showInstallPrompt, useSw } from '../lib/sw';
import { useLiveProfile } from '../lib/use-profile';
import { applyThemeColor, type SchemeChoice } from '../lib/theme-color';
import { Segmented } from '../components/Segmented';
import { CuratorChannels } from './curator/Channels';
import { CuratorPieces } from './curator/Pieces';
import { CuratorVideos } from './curator/Videos';
import {
  clampTranscriptSize,
  enVisibilityWrite,
  EN_OPTIONS,
  keepPlayingWrite,
  storageLine,
  transcriptSizeWrite,
  TRANSCRIPT_SIZE_DEFAULT,
  TRANSCRIPT_SIZE_MAX,
  TRANSCRIPT_SIZE_MIN,
} from './me-settings';

/** The account card's entitlement line (04-flows F15 "Account"; design frame 7c). */
export function entitlementLine(me: MeType): string {
  const e = me.entitlement;
  if (!e.active || e.plan === 'none')
    return 'No active plan — glossing, saving and TTS are part of the paid layer';
  if (e.plan === 'comp') return 'nelnela — complimentary access';
  const until = e.endsAt
    ? ` — until ${new Date(e.endsAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}`
    : '';
  const name = e.plan === 'pcv' ? 'Peace Corps' : e.plan;
  return `nelnela — ${name} plan${until}`;
}

/**
 * F15 Me — the account card, the Coverage report (M4a), and, from M5, the App card (install,
 * version, storage, update) and the Settings card (frame 7c). Role-gated curator tools below.
 *
 * M4c: a "Your media" row appears here for a learner whose personal lane is enabled — i.e. when
 * `me.media !== null`, which is Jeff and nobody else (ADR-0006 §5). When it is null this screen
 * is byte-for-byte what it was: no row, no placeholder, no explanation. Absence of the feature,
 * not a disabled version of it.
 */
export function Me() {
  const { me } = useStudy();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [signingOut, setSigningOut] = useState(false);
  const curator = me.learner.role === 'curator' || me.learner.role === 'admin';
  // Live, not the boot snapshot: a settings write invalidates ['me'], and this is what makes
  // the control keep the value it just wrote instead of snapping back.
  const profile = useLiveProfile(me.profiles[0] ?? null);

  const signOut = async () => {
    setSigningOut(true);
    try {
      // Before the session goes: this learner's cached reads and audio are theirs, and the next
      // person to open this browser is not necessarily them.
      await purgeUserCaches();
      await authClient.signOut();
    } finally {
      qc.clear(); // nothing of this learner survives in memory for the next sign-in
      nav('/signin', { replace: true });
    }
  };

  return (
    <Container size={curator ? 960 : 640} py="lg">
      <Stack gap="lg">
        <Title order={2}>Me</Title>

        <Link to="/me/coverage" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div
            style={{
              background: 'var(--nn-panel)',
              border: '1px solid var(--nn-hairline)',
              borderRadius: 12,
              padding: '14px 16px',
            }}
          >
            <Text fw={600}>Coverage</Text>
            <Text fz="sm" c="dimmed" mt={4}>
              What the app has worked out about your Georgian, and how sure it is →
            </Text>
          </div>
        </Link>

        <div
          style={{
            background: 'var(--nn-panel)',
            border: '1px solid var(--nn-hairline)',
            borderRadius: 12,
            padding: '14px 16px',
          }}
        >
          <Text fw={600}>{me.learner.displayName ?? 'you'}</Text>
          <Text fz="sm" c="dimmed" mt={4}>
            {entitlementLine(me)}
          </Text>
          <Button variant="default" size="xs" mt="md" onClick={signOut} loading={signingOut}>
            Sign out
          </Button>
        </div>

        {profile && <LevelCard profile={profile} />}

        {profile && <SettingsCard profile={profile} />}

        <AppCard />

        {me.media && <MediaCard seed={me.media} />}

        {curator && <CuratorSection />}
      </Stack>
    </Container>
  );
}

/** The 7c card: a stack of label-and-control rows behind one hairline, dividers between. */
function RowCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <Text fz={11} c="var(--nn-faint)" mb={6} style={{ letterSpacing: '.05em' }}>
        {title}
      </Text>
      <div
        style={{
          background: 'var(--nn-panel)',
          border: '1px solid var(--nn-hairline)',
          borderRadius: 12,
          padding: '8px 16px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ label, children, last }: { label: string; children: ReactNode; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 0',
        borderBottom: last
          ? undefined
          : '1px solid color-mix(in srgb, var(--nn-text) 5%, transparent)',
      }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--nn-text)', flex: 1, minWidth: 0 }}>{label}</span>
      {children}
    </div>
  );
}

/** A plain value on the right of a row (11.5px muted) — version, storage. */
function RowValue({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontSize: 11.5, color: 'var(--nn-muted)', flexShrink: 0 }}>{children}</span>
  );
}

/** A quiet action on the right of a row — Install, Reload. */
function RowAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{ fontSize: 11.5, color: 'var(--nn-accent-text)', flexShrink: 0 }}
    >
      {children}
    </UnstyledButton>
  );
}

/**
 * The installed app, reported rather than promised: whether it can be installed here, which
 * build is running, whether the browser agreed to keep the offline data, and whether a newer
 * build is waiting.
 */
function AppCard() {
  const { installPrompt, needRefresh, persisted, updateSW } = useSw();
  const standalone = isStandalone();
  const canInstall = !!installPrompt && !standalone;
  return (
    <RowCard title="APP">
      {canInstall && (
        <Row label="Install nelnela on this device">
          <RowAction onClick={() => void showInstallPrompt()}>Install</RowAction>
        </Row>
      )}
      <Row label="Version">
        <RowValue>{import.meta.env.VITE_APP_VERSION}</RowValue>
      </Row>
      <Row label="Offline storage" last={!needRefresh}>
        <RowValue>{storageLine(persisted)}</RowValue>
      </Row>
      {needRefresh && (
        <Row label="Reload for the new version" last>
          <RowAction onClick={() => void updateSW?.(true)}>Reload</RowAction>
        </Row>
      )}
    </RowCard>
  );
}

const SCHEME_OPTIONS: ReadonlyArray<{ value: SchemeChoice; label: string }> = [
  { value: 'auto', label: 'System' },
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

/**
 * Frame 7c's settings rows — the ones this app can actually back today.
 *
 * Deliberately absent: the script (Mkhedruli/Latin) toggle and the reminder time. Neither is
 * wired to anything yet, and a switch that does nothing is worse than no switch.
 *
 * Appearance is device-scoped and so lives in localStorage, unlike the three display settings
 * below it, which are the learner's preferences and follow them to any device through the
 * profile (M4a decision 11).
 */
/**
 * M10-iii: where the placement wizard lives after set-up.
 *
 * α promises "run it again from Me, whenever" on the wizard's own last screen, so this row is the
 * other half of a sentence the learner has already read. The estimate is shown as a BAND and
 * hedged in the copy — the same hedge the wizard uses, for the same reason: it is at most a
 * hundred yes/no answers, and the memory model overrides it from real evidence anyway (the
 * standing rule is "level is a prior; MemoryState decides").
 */
function LevelCard({ profile }: { profile: Profile }) {
  const navigate = useNavigate();
  const level = profile.levelEstimate;
  const band = level === null ? null : Math.floor((level - 1) / 10) * 10 + 1;
  return (
    <RowCard title="LEVEL">
      <Row label="Your edge, for now" last>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <RowValue>{band === null ? 'not set' : `levels ${band}–${band + 9}`}</RowValue>
          <RowAction onClick={() => navigate('/placement')}>
            {band === null ? 'Find it' : 'Re-run'}
          </RowAction>
        </span>
      </Row>
    </RowCard>
  );
}

function SettingsCard({ profile }: { profile: Profile }) {
  const qc = useQueryClient();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const display = profile.display;

  // Optimistic: a switch must move under the finger, not after a round trip to Frankfurt.
  const [pending, setPending] = useState<Partial<Profile['display']>>({});
  const save = useMutation({
    mutationFn: (body: ProfileUpdate) => api.profileUpdate(profile.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
    onSettled: () => setPending({}),
  });
  const write = (body: ProfileUpdate) => {
    setPending((p) => ({ ...p, ...body.display }));
    save.mutate(body);
  };

  const en = (pending.enVisibility ?? display.enVisibility) as EnMode;
  const keepPlaying = pending.keepPlayingWhileGlossing ?? display.keepPlayingWhileGlossing;
  const size = clampTranscriptSize(
    pending.transcriptSize ?? display.transcriptSize ?? TRANSCRIPT_SIZE_DEFAULT,
  );

  const setScheme = (choice: SchemeChoice) => {
    setColorScheme(choice);
    // Mantine repaints the app; the browser chrome is ours to keep in step.
    applyThemeColor(choice);
  };

  return (
    <RowCard title="SETTINGS">
      <Row label="Appearance">
        <Segmented
          label="Appearance"
          value={colorScheme as SchemeChoice}
          options={SCHEME_OPTIONS}
          onChange={setScheme}
        />
      </Row>
      <Row label="English translations">
        <Segmented
          label="English translations"
          value={en}
          options={EN_OPTIONS}
          onChange={(v) => write(enVisibilityWrite(v))}
        />
      </Row>
      <Row label="keep playing while I read glosses">
        <Switch
          checked={keepPlaying}
          onChange={(e) => write(keepPlayingWrite(e.currentTarget.checked))}
          aria-label="keep playing while I read glosses"
        />
      </Row>
      <Row label="Georgian text size" last>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 9,
            border: '1px solid color-mix(in srgb, var(--nn-text) 14%, transparent)',
            borderRadius: 7,
            padding: '3px 10px',
            fontSize: 12,
            color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
          }}
        >
          <UnstyledButton
            onClick={() => write(transcriptSizeWrite(size - 1))}
            disabled={size <= TRANSCRIPT_SIZE_MIN}
            aria-label="Smaller Georgian text"
            style={{ opacity: size <= TRANSCRIPT_SIZE_MIN ? 0.4 : 1, fontSize: 12 }}
          >
            −
          </UnstyledButton>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{size}</span>
          <UnstyledButton
            onClick={() => write(transcriptSizeWrite(size + 1))}
            disabled={size >= TRANSCRIPT_SIZE_MAX}
            aria-label="Larger Georgian text"
            style={{ opacity: size >= TRANSCRIPT_SIZE_MAX ? 0.4 : 1, fontSize: 12 }}
          >
            +
          </UnstyledButton>
        </span>
      </Row>
    </RowCard>
  );
}

/**
 * M4c "Your media" — what the uploads occupy and what that costs. Report tone, no progress bar:
 * there is no cap to fill, so a bar would invent a limit that does not exist. The cost line is
 * the reason this row exists at all — Jeff should be able to read the bucket's monthly bill off
 * his own account screen rather than the Cloudflare dashboard.
 */
function MediaCard({ seed }: { seed: MediaUsage }) {
  // `me` is fetched once at boot and StudyProvider memoizes it, so the boot snapshot goes stale
  // the moment something is uploaded. The wizard writes this same key after a successful add, so
  // reading it here is what makes "N videos · X GB stored" true when Jeff comes back to Me.
  const usage =
    useQuery({
      queryKey: ['media', 'usage'],
      queryFn: () => api.mediaUsage(),
      initialData: seed,
      staleTime: 60_000,
    }).data ?? seed;
  return (
    <div
      style={{
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <Text fw={600}>Your media</Text>
      <Text fz="sm" c="dimmed" mt={4}>
        {mediaUsageLine(usage)}
      </Text>
      {usage.objects > 0 && (
        <Text fz="sm" c="dimmed" mt={2}>
          {mediaCostLine(usage.usedBytes)}
        </Text>
      )}
    </div>
  );
}

/** Curator tools (04-flows F15 — "plain tables, deliberately undesigned"); the server 403s too. */
function CuratorSection() {
  const [tab, setTab] = useState<'channels' | 'videos' | 'pieces'>('channels');
  return (
    <Stack gap="sm">
      <Title order={3}>Curator</Title>
      <SegmentedControl
        value={tab}
        onChange={(v) => setTab(v as 'channels' | 'videos' | 'pieces')}
        data={[
          { value: 'channels', label: 'Channels' },
          { value: 'videos', label: 'Videos' },
          { value: 'pieces', label: 'Pieces' },
        ]}
        w="fit-content"
      />
      {tab === 'channels' ? (
        <CuratorChannels />
      ) : tab === 'videos' ? (
        <CuratorVideos />
      ) : (
        <CuratorPieces />
      )}
    </Stack>
  );
}
